import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PaymentsLinearChart from "./payments";
import LocationsSalesChart from "./locations-sales";
import StatisticsBox from "./statistics-box";
import totalSalesIcon from "src/assets/svg-icons/total-sales.svg";
import paidIcon from "src/assets/svg-icons/paid.svg";
import freeIcon from "src/assets/svg-icons/free.svg";
import SalesTable from "./sales-table";
import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { formatNumber } from "src/utils";
import { useGetLocationsList } from "src/api/locations";
import { notificationsState } from "src/store/notifications";
import { useRecoilState } from "recoil";
import { useGetSalesSummary } from "src/api/sales";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { useGetClientsList } from "src/api/clients";
import { IClientResponse } from "src/types/clients";
import { LoadingButton } from "@mui/lab";
import { NoData } from "src/shared/components/tables/no-data";
import { ICity } from "src/types/generic";
import { useGetCitiesList } from "src/api/general";
import CustomAutocomplete from "src/shared/components/custom-auto-complete";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const initStartDate = moment().startOf("month").hour(8).toDate();
const initEndDate = moment().startOf("day").add(1, "days").hour(8).toDate();

const ReportsSalesSummaryPage = () => {
  const [selectedLocations, setSelectedLocations] = useState<any[]>([]);
  const [selectedCity, setSelectedCity] = useState<ICity>();
  const [tempSelectedLocations, setTempSelectedLocations] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState<IClientResponse>();
  const [tempSelectedClient, setTempSelectedClient] =
    useState<IClientResponse>();
  const [tempSelectedCity, setTempSelectedCity] = useState<ICity>();
  const [tempSelectedService, setTempSelectedService] = useState<string>("all");
  const [selectedService, setSelectedService] = useState<string>("all");
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(
    initStartDate,
  );
  const [startDate, setStartDate] = useState<Date | null>(initStartDate);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(initEndDate);
  const [endDate, setEndDate] = useState<Date | null>(initEndDate);
  const [openStartDate, setOpenStartDate] = useState<boolean>(false);
  const [openEndDate, setOpenEndDate] = useState<boolean>(false);
  const [tempLocationType, setTempLocationType] = useState<string>("all");
  const [locationType, setLocationType] = useState<string>("all");

  const minDate = new Date("2024-01-01T00:00:00");
  const maxDate = moment().add(1, "year").endOf("year").toDate();

  const filters = {
    ...(selectedClient && { userId: selectedClient.id }),
    ...(selectedLocations.length > 0 && {
      locations: selectedLocations.map((loc) => loc.id),
    }),
    ...(selectedCity && { cities: [selectedCity.id] }),
    ...(selectedService !== "all" && { serviceType: selectedService }),
    ...(locationType !== "all" && { locationType: locationType }),
    ...(startDate && { startDate: startDate?.getTime() }),
    ...(endDate && { endDate: endDate?.getTime() }),
  };

  // APIS
  const { data: locationsData } = useGetLocationsList();
  const { data: clientsData } = useGetClientsList();
  const { data, isLoading, error, status } = useGetSalesSummary(filters);
  const { data: citiesData } = useGetCitiesList({
    type: "active",
  });

  const handleChangeLocations = (value: any[], reason: string) => {
    setTempSelectedLocations(value);
  };

  const handleChangeClient = (event: React.SyntheticEvent, newValue: any) => {
    setTempSelectedClient(newValue);
  };

  const handleChangeTypeOfService = (e: SelectChangeEvent) => {
    const value = e.target.value;
    setTempSelectedService(value);
  };

  const handleChangeCity = (event: React.SyntheticEvent, newValue: any) => {
    setTempSelectedCity(newValue);
  };

  const handleSelectLocationType = (e: SelectChangeEvent) => {
    const value = e.target.value;
    setTempLocationType(value);
  };

  const handleApply = () => {
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setSelectedCity(tempSelectedCity);
    setSelectedLocations(tempSelectedLocations);
    setSelectedService(tempSelectedService);
    setSelectedClient(tempSelectedClient);
    setLocationType(tempLocationType);
  };

  const handleResetFilers = () => {
    setStartDate(initStartDate);
    setEndDate(initEndDate);
    setSelectedLocations([]);
    setSelectedService("all");
    setLocationType("all");
    setSelectedClient(undefined);
    setSelectedCity(undefined);

    setTempStartDate(initStartDate);
    setTempEndDate(initEndDate);
    setTempSelectedLocations([]);
    setTempSelectedService("all");
    setTempLocationType("all");
    setTempSelectedClient(undefined);
    setTempSelectedCity(undefined);
  };

  const sortedLocations = useMemo(() => {
    return (
      locationsData?.locations
        .slice()
        .sort((a: any, b: any) => a.nameEn?.localeCompare(b.nameEn)) ?? []
    );
  }, [locationsData]);

  const sortedClients = useMemo(() => {
    return (
      clientsData?.data
        .slice()
        .sort((a, b) =>
          a.clientInfo.nameEn?.localeCompare(b.clientInfo.nameEn),
        ) || []
    );
  }, [clientsData]);

  const filteredLocations = useMemo(() => {
    return tempSelectedClient
      ? sortedLocations.filter(
          (loc: any) => loc.clientId === tempSelectedClient?.id,
        )
      : sortedLocations;
  }, [tempSelectedClient, sortedLocations]);

  const filteredCities = useMemo(() => {
    if (!citiesData?.data) return [];

    if (tempSelectedLocations.length === 0 && !tempSelectedClient) {
      return citiesData.data;
    }

    const availableLocations =
      tempSelectedLocations.length > 0
        ? tempSelectedLocations
        : filteredLocations;

    const availableLocationsCitiesIds = availableLocations.map(
      (loc: any) => loc.cityId,
    );

    return citiesData.data.filter((city) =>
      availableLocationsCitiesIds.includes(city.id),
    );
  }, [
    tempSelectedLocations,
    citiesData,
    filteredLocations,
    tempSelectedClient,
  ]);

  useEffect(() => {
    setTempSelectedLocations([]);
    setTempSelectedCity(undefined);
  }, [tempSelectedClient]);

  useEffect(() => {
    setTempSelectedCity(undefined);
  }, [tempSelectedLocations]);

  useEffect(() => {
    if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            error?.response?.data?.message ?? "Oops! Something went wrong.",
        },
      ]);
    }
  }, [status]);

  return (
    <Box p={{ xs: "20px 12px", md: "30px 20px", lg: "30px 30px" }}>
      <Typography fontWeight={600} fontSize={"22px"} mb={{ xs: 2, md: 4 }}>
        Sales Summary
      </Typography>

      {/* filters */}
      <Stack direction={{ xs: "column", md: "row" }} gap={"10px"} mb={3}>
        <Stack direction={"row"} gap={"10px"} flexWrap={"wrap"} flexGrow={1}>
          {/* clients */}
          <CustomAutocomplete
            key={tempSelectedClient?.id ?? "client-autocomplete"}
            sx={{ width: { xs: "100%", sm: "220px" } }}
            size="small"
            disablePortal
            id="combo-box-demo"
            options={sortedClients}
            getOptionLabel={(option) => option.clientInfo.nameEn}
            getOptionKey={(option) => `${option.id}`}
            value={tempSelectedClient}
            onChange={handleChangeClient}
            renderInput={(params: any) => (
              <TextField fullWidth {...params} label={"Client"} />
            )}
          />

          {/* locations */}
          <CustomAutocomplete
            sx={{ width: { xs: "100%", sm: "220px" } }}
            size="small"
            limitTags={1}
            multiple
            id="tags-outlined"
            options={filteredLocations}
            disableCloseOnSelect
            getOptionLabel={(option: any) => option.nameEn}
            getOptionKey={(option: any) => option.id}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={`${props.id} ${option.id}`}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  checked={selected}
                  size="small"
                  sx={{ padding: "3px 0px", mr: "5px" }}
                />
                <Typography>{option.nameEn}</Typography>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Location" placeholder="" />
            )}
            renderTags={(selected, getTagProps) =>
              selected.map((option: any, index: number) => (
                <Chip
                  size="small"
                  label={option.nameEn}
                  {...getTagProps({ index })}
                  key={option.id}
                />
              ))
            }
            value={tempSelectedLocations}
            onChange={(event, value, reason) => {
              handleChangeLocations(value, reason);
            }}
          />
          {/* location type */}
          <FormControl size="small" sx={{ width: { xs: "100%", sm: "140px" } }}>
            <InputLabel id="location-type-select-label">
              Location Type
            </InputLabel>
            <Select
              data-testid="filter-location-type-select"
              labelId="location-type-select-label"
              id="location-type-select"
              value={tempLocationType}
              label="Location Type"
              onChange={handleSelectLocationType}
            >
              <MenuItem value={"all"}>All</MenuItem>
              <MenuItem value={"regular"}>Regular</MenuItem>
              <MenuItem value={"event"}>Event</MenuItem>
            </Select>
          </FormControl>
          {/* city */}
          <CustomAutocomplete
            key={tempSelectedCity?.id ?? "city-autocomplete"}
            sx={{ width: { xs: "100%", sm: "170px" } }}
            size="small"
            disablePortal
            id="combo-box-demo"
            options={filteredCities}
            getOptionLabel={(option) => option.nameEn}
            getOptionKey={(option) => `${option.id}`}
            value={tempSelectedCity}
            onChange={handleChangeCity}
            renderInput={(params: any) => (
              <TextField fullWidth {...params} label={"City"} />
            )}
          />
          {/* service */}
          <FormControl
            fullWidth
            size="small"
            sx={{ width: { xs: "100%", sm: "190px" } }}
          >
            <InputLabel id="demo-simple-select-label">Service</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={tempSelectedService}
              label="Service"
              onChange={handleChangeTypeOfService}
              fullWidth
            >
              <MenuItem value={"all"}>All</MenuItem>
              <MenuItem value={"valet"}>Valet</MenuItem>
              <MenuItem value={"selfParking"}>Self Parking</MenuItem>
              <MenuItem value={"golfCar"}>Golf Cart</MenuItem>
              <MenuItem value={"partialCarWash"}>Partial Car Wash</MenuItem>
              <MenuItem value={"fullCarWash"}>Complete Car Wash</MenuItem>
            </Select>
          </FormControl>
          {/* start date */}
          <DateTimePicker
            label="Start Date"
            slotProps={{
              textField: {
                size: "small",
                onClick: () => setOpenStartDate(true),
                sx: { width: "185px" },
                inputProps: { readOnly: true },
              },
            }}
            format={"dd/MM/yyyy hh:mm aa"}
            value={tempStartDate}
            onChange={(newValue) => setTempStartDate(newValue)}
            monthsPerRow={4}
            name="start date"
            disableOpenPicker
            open={openStartDate}
            onClose={() => setOpenStartDate(false)}
            minDate={minDate}
            maxDate={maxDate}
          />
          {/* end date */}
          <DateTimePicker
            label="End Date"
            slotProps={{
              textField: {
                size: "small",
                onClick: () => setOpenEndDate(true),
                sx: { width: "185px" },
                inputProps: { readOnly: true },
              },
            }}
            format={"dd/MM/yyyy hh:mm aa"}
            value={tempEndDate}
            onChange={(newValue) => setTempEndDate(newValue)}
            name="end date"
            disableOpenPicker
            open={openEndDate}
            onClose={() => setOpenEndDate(false)}
            minDate={minDate}
            maxDate={maxDate}
          />
        </Stack>
        <Stack direction={"row"}>
          <LoadingButton onClick={handleResetFilers}>Clear</LoadingButton>
          <LoadingButton
            loading={isLoading}
            variant="contained"
            onClick={handleApply}
            sx={{ width: "100px" }}
          >
            Apply
          </LoadingButton>
        </Stack>
      </Stack>

      {isLoading ? (
        <Stack gap={0.9} pb={2}>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17].map(
            (item) => (
              <Skeleton
                key={item}
                width={"100%"}
                height={"40px"}
                variant="rectangular"
              />
            ),
          )}
        </Stack>
      ) : (
        <>
          {!data ? (
            <NoData height={"600px"} />
          ) : (
            <>
              <PaymentsLinearChart data={data?.data.salesByDay} />
              <Grid container mt={0} spacing={"20px"} mb={"20px"}>
                <Grid item xs={12} lg={8}>
                  <LocationsSalesChart data={data.data.salesByLocation} />
                </Grid>
                <Grid
                  item
                  xs={12}
                  lg={4}
                  display={"flex"}
                  direction={"column"}
                  gap={"10px"}
                >
                  <StatisticsBox
                    label="Total Sales"
                    value={`${formatNumber(
                      data.data.overallSummary.totalSales,
                    )}`}
                    icon={totalSalesIcon}
                    isMoney
                  />
                  <StatisticsBox
                    label="Paid Tickets"
                    value={`${formatNumber(
                      data.data.overallSummary.ticketPaidCount,
                    )}`}
                    icon={paidIcon}
                  />
                  <StatisticsBox
                    label="Free Tickets"
                    value={`${formatNumber(
                      data.data.overallSummary.freeTicketCount,
                    )}`}
                    icon={freeIcon}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </>
      )}
      <SalesTable
        startDate={startDate}
        endDate={endDate}
        selectedLocations={selectedLocations}
        selectedService={selectedService}
        locationType={locationType}
        selectedClient={selectedClient}
        selectedCity={selectedCity}
      />
    </Box>
  );
};

export default ReportsSalesSummaryPage;
